import {
  KeyboardEvent,
  cloneElement,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { debounce } from 'lodash';
import { AutoComplete, AutoCompleteChangeEvent, ListItemProps } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';

import classes from './HeaderSearch.module.scss';

export interface HeaderSearchSuggestion {
  title: string;
  subtitle?: string;
  tag?: string;
}

interface HeaderSearchProps<T> {
  placeholder: string;
  suggestions: T[];
  loading?: boolean;
  toSuggestion: (item: T) => HeaderSearchSuggestion;
  // responses can return out of order; the caller must ignore any that isn't for the latest query
  onQueryChange: (query: string) => void;
  onSelect: (item: T) => void;
  onSearch?: (query: string) => void;
  onClear?: () => void;
  minLength?: number;
  maxLength?: number;
  debounceMs?: number;
}

const HeaderSearch = <T, >({
  placeholder,
  suggestions,
  loading = false,
  toSuggestion,
  onQueryChange,
  onSelect,
  onSearch,
  onClear,
  minLength = 3,
  maxLength,
  debounceMs = 700,
}: HeaderSearchProps<T>): JSX.Element => {
  const [ query, setQuery ] = useState('');
  const [ listOpen, setListOpen ] = useState(false);
  const [ mobileOpen, setMobileOpen ] = useState(false);
  // typed but not yet sent, so the current suggestions belong to an older query
  const [ pending, setPending ] = useState(false);
  // an Enter that picked a highlighted suggestion shouldn't also run a search
  const pickedByKeyRef = useRef(false);

  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;
  const debouncedQueryChange = useMemo(
    () => debounce((value: string) => {
      setPending(false);
      onQueryChangeRef.current(value);
    }, debounceMs),
    [ debounceMs ]
  );
  useEffect(() => () => debouncedQueryChange.cancel(), [ debouncedQueryChange ]);

  const tooLong = maxLength !== undefined && query.length > maxLength;
  const canSearch = query.length >= minLength && !tooLong;

  const handleChange = useCallback((event: AutoCompleteChangeEvent) => {
    const { type } = event.syntheticEvent ?? {};
    if (type !== 'change') {
      // a picked suggestion or the clear button
      setListOpen(false);
      // Enter on a highlighted row hands over the suggestion itself, since no textField is set
      if (type === 'keydown' && event.value && typeof event.value === 'object') {
        pickedByKeyRef.current = true;
        onSelect(event.value as T);
        return;
      }
      if (!event.value) {
        setQuery('');
        setPending(false);
        debouncedQueryChange.cancel();
        onClear?.();
      }
      return;
    }
    setQuery(event.value);
    const longEnough = event.value.length >= minLength;
    setListOpen(longEnough);
    setPending(longEnough);
    if (longEnough) {
      debouncedQueryChange(event.value);
    } else {
      debouncedQueryChange.cancel();
    }
  }, [ minLength, debouncedQueryChange, onClear, onSelect ]);

  const search = useCallback(() => {
    if (onSearch && canSearch) {
      setListOpen(false);
      onSearch(query);
    }
  }, [ onSearch, canSearch, query ]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') {
      return;
    }
    if (pickedByKeyRef.current) {
      pickedByKeyRef.current = false;
      return;
    }
    if (!loading) {
      search();
    }
  }, [ loading, search ]);

  const toggleMobile = useCallback(() => setMobileOpen((open) => !open), []);

  const renderItem = useCallback((li: ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
    const item = itemProps.dataItem as T;
    const { title, subtitle, tag } = toSuggestion(item);
    const row = (
      <div className="result" onClick={() => onSelect(item)}>
        <span className="headline">
          {title}
          {subtitle && <span className="detail">{subtitle}</span>}
        </span>
        {tag && <span className="tag">{tag}</span>}
      </div>
    );
    // keep Kendo's li handlers so picking an item still closes the list
    return cloneElement(li, li.props, row);
  }, [ toSuggestion, onSelect ]);

  return (
    <div className={cx(classes.headerSearch, { expanded: mobileOpen })} onKeyDown={handleKeyDown}>
      <div className="form">
        <AutoComplete
          data={suggestions}
          value={query}
          loading={loading || pending}
          opened={listOpen && !loading && !pending}
          clearButton
          onChange={handleChange}
          onFocus={() => setListOpen(query.length >= minLength)}
          onBlur={() => setListOpen(false)}
          itemRender={renderItem}
          className="field"
          placeholder={placeholder}
          popupSettings={{ className: classes.headerSearchPopup }}
        />
        {tooLong && <div className="k-form-error">Enter max {maxLength} characters.</div>}
        <Button fillMode="link" className="dismiss" type="button" aria-label="Close search" onClick={toggleMobile} />
        <div className="overlay" onClick={toggleMobile} />
        {onSearch && (
          <Button fillMode="link" className="submit submitMobile" type="button" disabled={!canSearch} onClick={search}>
            Search
          </Button>
        )}
      </div>
      {onSearch && (
        <Button fillMode="link" className="submit submitDesktop" type="button" disabled={!canSearch || loading} onClick={search}>
          Search
        </Button>
      )}
      <Button fillMode="link" className="toggle" type="button" aria-label="Search" onClick={toggleMobile} />
    </div>
  );
};

export default HeaderSearch;
